import { useState, useRef, useEffect } from 'react'
import { Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LANGUAGE_KEY } from '@/i18n'

const languages = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Francais' },
  { code: 'zh', label: '中文' },
] as const

export default function LanguageSelector() {
  const { i18n, t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close on outside click — same pattern as FilterPanel dropdowns
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code)
    localStorage.setItem(LANGUAGE_KEY, code)
    document.documentElement.lang = code
    setIsOpen(false)
  }

  const currentLabel = languages.find(l => l.code === i18n.language)?.label ?? 'EN'
  const otherLanguages = languages.filter(l => l.code !== i18n.language)

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
        aria-label={t('language.changeLanguage')}
      >
        <Globe className="w-4 h-4" />
        <span className="font-medium">{currentLabel}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[80px]">
          {otherLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-brand-50 hover:text-brand transition-colors"
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
